/**
 * Enhanced API client with authentication
 *
 * This module provides transparent authentication for all API requests.
 * Cookies are sent automatically by the browser (withCredentials).
 * No manual token management needed.
 *
 * Features:
 * - Automatic cookie-based authentication
 * - 401 response detection and redirect to login
 * - Graceful error handling
 */

import superagent, {
  SuperAgentRequest,
  Response,
  ResponseError,
} from 'superagent';

/**
 * Wraps a superagent request to handle authentication
 * Cookies are sent automatically by browser
 * @param request - The superagent request to wrap
 * @returns Promise resolving to the response
 */
export async function withAuth(request: SuperAgentRequest): Promise<Response> {
  try {
    return await request;
  } catch (error) {
    // Check if it's a 401 error (token invalid/revoked)
    if (error && typeof error === 'object' && 'status' in error) {
      const responseError = error as ResponseError;
      if (responseError.status === 401) {
        // Token was revoked or expired - redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      }
    }

    // Re-throw error for caller to handle
    throw error;
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: (url: string) => withAuth(superagent.get(url)),
  post: (url: string) => withAuth(superagent.post(url)),
  put: (url: string) => withAuth(superagent.put(url)),
  patch: (url: string) => withAuth(superagent.patch(url)),
  delete: (url: string) => withAuth(superagent.delete(url)),
};

/**
 * Export the withAuth function as default for easy usage
 *
 * Usage examples:
 *
 * 1. Wrap existing superagent calls:
 *    const response = await withAuth(superagent.get('/api/data'));
 *
 * 2. Use the convenience API:
 *    const response = await api.get('/api/data');
 *
 * 3. Use createAuthenticatedRequest directly:
 *    const request = createAuthenticatedRequest('GET', '/api/data');
 *    const response = await request.query({ foo: 'bar' });
 */
export default withAuth;
